import React, { useState } from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { PencilIcon } from "Shared/Icons";
import InfoTooltip from "Shared/InfoTooltip";
import styled from "styled-components";
import { patchPaymentsRequest } from "src/store/actions/payments";
import { parseCurrencyToFloat } from "src/utils";
import { getSelectedPaymentType } from "src/utils/vehicles";
import { ApplicationStore, VehicleState } from "Types/state";
import { CurrencyInput } from "./CurrencyInput";
import { getVehiclePrice } from "src/store/selectors/vehicles";

const DownPaymentCell = styled("div")<{ inputError: boolean }>`
    align-items: center;
    border: ${(props) => (props.inputError ? `1px solid ${props.theme.palette.error.main}` : "none")};
    display: flex;
    height: 100%;
    padding: 0 ${(props) => (props.inputError ? "1.5em" : "0")} 0 0;
    width: 100%;
`;

const StyledPencilIcon = styled(PencilIcon)`
    color: ${(props) => props.theme.palette.grey[70]};
    margin-right: 0.5em;

    ${(props) => props.theme.breakpoints.up("tablet")} {
        margin-right: 1em;
    }
`;

interface Props {
    title?: string;
    uuid: string;
    value: number;
    vehicle: Readonly<VehicleState>;
}

/**
 * Shim for a down payment specific CurrencyInput
 */
const DownPaymentInputComponent = ({
    t,
    title,
    updateDownPaymentForVehicle,
    uuid,
    value,
    vehicle,
    vehiclePrice,
}: Props & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & WithTranslation) => {
    const [inputError, setInputError] = useState(false);

    const handleCurrencyUpdate = (newInput: number) => {
        if (vehiclePrice && newInput >= vehiclePrice) {
            setInputError(true);
            return;
        }
        setInputError(false);
        updateDownPaymentForVehicle(uuid, vehicle, newInput);
    };

    return (
        <React.Fragment>
            <DownPaymentCell inputError={inputError} title={title}>
                <CurrencyInput
                    value={parseCurrencyToFloat(value)}
                    maxValue={vehiclePrice}
                    minValue={0}
                    updateValue={handleCurrencyUpdate}
                />
                {inputError && <InfoTooltip text={t("MyGarage.TableCompare.Cells.CellEditableText.error")} />}
            </DownPaymentCell>
            <StyledPencilIcon />
        </React.Fragment>
    );
};

const mapStateToProps = (state: ApplicationStore, { vehicle }: Props) => {
    return {
        uuid: state.deal.uuid || "",
        vehiclePrice: getVehiclePrice(state, vehicle.vin),
    };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
    return {
        updateDownPaymentForVehicle: (uuid: string, vehicle: VehicleState, value: number) => {
            dispatch(
                patchPaymentsRequest(
                    uuid,
                    vehicle.vin,
                    {
                        [`${getSelectedPaymentType(vehicle)}`]: {
                            down_payment: value,
                        },
                    },
                    "set_down_payment"
                )
            );
        },
    };
};

const DownPaymentInput = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(DownPaymentInputComponent));

export default DownPaymentInput;
